// Lote 79 — regras do Painel inicial (puras, sem React): o que é "a ação mais crítica agora",
// countdown, dicas do dia. Ordem de prioridade combinada com o Robson (19/10/2026):
//   1. prazo/audiência de HOJE (o mais próximo); 2. conflito de horário; 3. ata para baixar;
//   4. publicação para validar/revisar.
// Lote 97 (pedido do Robson, 22/10/2026): prazo VENCIDO sem baixa NÃO é mais "Ação agora" nem
// mensagem do letreiro — fica só no bloco vermelho "Prazos vencidos sem baixa" (com "Feito").
// Nada cujo horário já passou aparece como "hoje às HH:MM"; todo card tem baixa em 1 toque.

const pad = (n) => String(n).padStart(2, "0");
export const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const todayIso = (now = new Date()) => isoOf(now);
export const addDaysIso = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return isoOf(d);
};

export const minutesOf = (hhmm) => {
  if (!hhmm || !String(hhmm).includes(":")) return null;
  const [h, m] = String(hhmm).split(":").map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

// Nome curto do compromisso. Lote 103: a descrição de algumas publicações é um texto enorme com link —
// nunca vai inteira para a tela (sem URL, no máximo ~70 caracteres).
const shortText = (t, max = 70) => {
  const clean = String(t || "").replace(/https?:\/\/\S+/g, "").replace(/\s+/g, " ").trim();
  return clean.length > max ? `${clean.slice(0, max - 1).trimEnd()}…` : clean;
};
export const itemLabel = (h) => shortText(h?.parties) || h?.case_number || shortText(h?.description) || shortText(h?.court) || "compromisso";

// "faltam 2h32" / "faltam 25 min" — só quando há horário e ele ainda não passou.
export const countdownLabel = (hhmm, now = new Date()) => {
  const t = minutesOf(hhmm);
  if (t == null) return null;
  const diff = t - (now.getHours() * 60 + now.getMinutes());
  if (diff < 0) return null;
  if (diff === 0) return "agora";
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return h > 0 ? `faltam ${h}h${pad(m)}` : `faltam ${m} min`;
};

/**
 * Lista ordenada de ações do Painel. Cada item: { key, tone, title, detail, to, pubId?, canDone?, baixa?, dismiss? }
 *  baixa: { kind: "prazo"|"ata"|"conflito", label, primary } — botão de 1 toque que resolve e tira o card.
 *  tone: "danger" | "warn" | "info" | "ok"
 * Entradas: overdue (de /painel/resumo), upcoming (de /hearings/upcoming), conflictMessage,
 * toValidate, reviewTotal.
 */
export function buildActions({ overdue = [], overdueOlder = 0, upcoming = [], conflictMessage = null, conflictTo = "/calendario", conflictKey = null,
  atas = [], toValidate = 0, reviewTotal = 0, ackedPubIds = [], now = new Date() }) {
  const out = [];
  const today = todayIso(now);
  const nowMin = now.getHours() * 60 + now.getMinutes();

  const todays = upcoming
    .filter((u) => u.hearing?.date === today)
    .map((u) => ({ u, t: minutesOf(u.hearing?.time) }))
    .filter(({ u, t }) => {
      const type = u.hearing?.event_type || "audiencia";
      if (type === "reuniao") return false;
      if (t != null && t < nowMin) return false; // Lote 97: horário já passou — não é mais "hoje às…"
      return true;
    })
    .sort((a, b) => (a.t ?? 9999) - (b.t ?? 9999));
  // Lote 102: "urgente" = prazo de hoje sem baixa, audiência nas próximas 2 h, conflito de horário
  // de hoje. Só o urgente vira mensagem VERMELHA do letreiro; o resto segue como mensagem normal.
  const isPrazoOf = (u) => (u.hearing?.event_type || "audiencia") === "prazo";
  // "Ciente" (Lote 102): o usuário limpou o alerta vermelho desse compromisso — segue como mensagem normal.
  const isUrgentToday = ({ u, t }) => !ackedPubIds.includes(u.pub_id) && (isPrazoOf(u) || (t != null && t - nowMin <= 120));
  if (todays.length > 0) {
    const pick = todays.find(isUrgentToday) || todays[0];
    const { u } = pick;
    const h = u.hearing || {};
    const isPrazo = isPrazoOf(u);
    const cd = countdownLabel(h.time, now);
    out.push({
      key: "today", tone: isPrazo ? "danger" : "warn", pubId: u.pub_id, canDone: isPrazo, to: `/audiencia/${u.pub_id}`,
      urgent: isUrgentToday(pick), label: itemLabel(h),
      baixa: isPrazo ? { kind: "prazo", label: "Feito", primary: true } : null,
      title: `${isPrazo ? "Prazo" : "Audiência"} hoje${h.time ? ` às ${h.time.slice(0, 5)}` : ""} — ${itemLabel(h)}${cd ? ` — ${cd}` : ""}`,
      detail: [isPrazo ? h.deadline_type : null, h.court_district || h.court].filter(Boolean).join(" · ") || (isPrazo ? "Vence hoje" : ""),
    });
  }

  if (conflictMessage) {
    out.push({ key: "conflict", tone: "warn", to: conflictTo || "/calendario", title: conflictMessage.replace(/^⚠️\s*/, ""), detail: "",
      urgent: !!conflictKey && String(conflictKey).startsWith(`${today}|`),
      conflictKey, toLabel: "Ver o dia", baixa: conflictKey ? { kind: "conflito", label: "Ciente", primary: false } : null });
  }

  // Lote 96: audiências dos últimos 7 dias cuja ata ainda não foi conferida (1 toque abre o compromisso).
  if (atas.length > 0) {
    const a = atas[0];
    out.push({
      key: "ata", tone: "warn", to: `/audiencia/${a.pub_id}`, pubId: a.pub_id, pendenciaId: a.pendencia_id,
      baixa: a.pendencia_id ? { kind: "ata", label: "Ata baixada", primary: true } : null,
      dismiss: a.pendencia_id ? { kind: "ata", label: "Dispensar" } : null,
      title: atas.length > 1
        ? `${atas.length} audiências recentes com ata para baixar`
        : `Ata para baixar — audiência de ${a.parties || a.case_number || "uma audiência"} (${(a.date || "").split("-").reverse().slice(0, 2).join("/")})`,
      detail: "Baixe a ata e agende os prazos.",
    });
  }

  const pend = Math.max(toValidate || 0, 0);
  if (pend > 0) {
    out.push({ key: "validate", tone: "info", to: "/revisao",
      title: `${pend} compromisso${pend > 1 ? "s" : ""} para validar`, detail: "Confira data e hora antes de seguir." });
  }
  if (reviewTotal > 0) {
    out.push({ key: "review", tone: "info", to: "/publicacoes?tipo=revisar",
      title: `${reviewTotal} publicaç${reviewTotal > 1 ? "ões" : "ão"} para revisar`, detail: "Abra e agende o prazo ou marque como conferida." });
  }
  return out;
}

// Dicas de uso — uma por dia (só aparecem na fase completa do letreiro).
export const TIPS = [
  "Dica: no Calendário (Dia/Semana), toque e segure um horário vazio para criar um evento nele.",
  "Dica: em Publicações, “Agendar prazo” já preenche o evento a partir do texto da intimação.",
  "Dica: “Conferido (nada a fazer)” tira a publicação de Revisar sem criar prazo.",
  "Dica: em Processos, “Atualizar andamentos” consulta só os processos cadastrados — é bem mais rápido.",
  "Dica: a busca de publicações continua em segundo plano; pode sair da tela que ela não para.",
  "Dica: marque o prazo como feito (✓) direto no Painel para ele sair da lista.",
  "Dica: no Calendário, Semana, toque num bloco para ler o texto completo e use “Expandir” para ver o dia todo.",
  "Dica: em Equipe, você compartilha um compromisso com outro usuário do IApauta.",
  "Dica: ajuste a antecedência dos lembretes em Configurações.",
  "Dica: a Calculadora de prazos conta dias úteis já descontando feriados nacionais.",
  "Dica: cole o texto de uma intimação ou ata em Novo evento — a IA sugere os prazos.",
];
export const tipOfTheDay = (now = new Date()) => {
  const start = new Date(now.getFullYear(), 0, 0);
  const day = Math.floor((now - start) / 86400000);
  return TIPS[day % TIPS.length];
};
